import React from "react";
import { Link } from "react-router-dom";

import { createStyleSheet } from "material-ui/styles";
import apiOptions from "./api-options";
import Grid from "material-ui/Grid";

const apiSpecs = [
  <li>
    <a
      target="_blank"
      rel="noopener noreferrer"
      href="https://headless-render-api.com/.well-known/open-api.yaml"
    >
      /.well-known/open-api.yaml
    </a>{" "}
    for OpenAPI/Swagger
  </li>,
  <li>
    <a
      target="_blank"
      rel="noopener noreferrer"
      href="https://headless-render-api.com/.well-known/ai-plugin.json"
    >
      /.well-known/ai-plugin.json
    </a>{" "}
    for LLMs like ChatGPT
  </li>,
];

const otherApiClients = [
  <li>
    <a
      target="_blank"
      rel="noopener noreferrer"
      href="https://github.com/sanfrancesco/prerendercloud-nodejs"
    >
      Node.js client
    </a>
  </li>,
].concat(apiSpecs);

const authInfo = [
  {
    name: "X-Prerender-Token",
    messages: [<code>curl --header "X-Prerender-Token: secret-token"</code>],
  },
];

const httpErrorCodes = [
  {
    name: 400,
    messages: [
      <strong>Invalid Request</strong>,
      "There will be an error message in the HTML, fix your request and retry",
      <div>
        <strong>Example causes:</strong>
        <ul>
          <li>malformed URL</li>
          <li>a localhost URL/IP</li>
          <li>
            or a page responds with{" "}
            <code>content-type: application/octet-stream</code>
          </li>
        </ul>
      </div>,
    ],
  },
  {
    name: 429,
    messages: [
      <strong>Rate limited</strong>,
      <div>
        Requests made without API tokens (or expired/missing billing
        information) will get this - see <Link to="/pricing">pricing</Link>
      </div>,
    ],
  },
  {
    name: 500,
    messages: [
      <strong>General Error</strong>,
      <div>
        <strong>Example causes</strong>:
        <ul>
          <li>
            10s (timeout) while waiting for a page to finish rendering (waits
            until all in-flight requests finish, load event, domContent event
            etc.)
          </li>
          <li>or HTTPS Page is making HTTP (non secure) requests</li>
          <li>...or a random bug on our end</li>
        </ul>
      </div>,
      "The error will show up in the headless-render-api.com web UI (after you sign in)",
    ],
  },
  {
    name: 502,
    messages: [
      <strong>Bad Gateway (your origin returned 5xx)</strong>,
      "It means we received a 5xx when trying to visit your page",
      "Or it means we received a 403 (forbidden). Typically this means your page is behind a login wall or a firewall (like Cloudflare) is blocking the headless-render-api user-agent (make an exception to allow user-agents matching /prerendercloud/)",
      "Probably not retryable. It depends on the site you're requesting. If it's your site, make sure it's up and running correctly",
    ],
  },
  {
    name: 503,
    messages: [
      <strong>Over capacity (Rare)</strong>,
      "Retry the request with some backoff",
      "We'll see the error and our autoscaler will increase capacity within 5 minutes, but you should email us anyway: support@headless-render-api.com",
    ],
  },
  {
    name: 504,
    messages: [
      <strong>Gateway Timeout (Rare)</strong>,
      "Retry the request with some backoff",
      "This is unexpected and should not happen. We'll see it, but you should email us anyway: support@headless-render-api.com",
    ],
  },
];

const styleSheet = createStyleSheet("API", (theme) => ({
  row: {
    padding: "10px 20px",
    "& h3": {
      fontSize: "16px",
      lineHeight: "24px",
    },
  },
  [theme.breakpoints.up("md")]: {
    row: {
      "& h3": {
        fontSize: "24px",
        lineHeight: "32px",
      },
    },
  },
  cellHeader: {
    fontWeight: "bold",
    fontSize: 16,
    // minWidth: 350,
    // maxWidth: 700,
  },
  apiList: {
    listStyleType: "none",
    margin: 0,
    padding: "0 0 0 16px",
    "& li": {
      fontSize: 14,
      marginTop: 10,
      marginBottom: 0,
      "& ul": {
        listStyleType: "none",
        paddingLeft: 20,
      },
      "& li": {
        marginBottom: 10,
        fontSize: 14,
      },
    },
    "& code": {
      fontSize: 12,
    },
  },
  inlinePre: {
    marginTop: "0 !important",
    marginBottom: "0 !important",
    paddingTop: "5px !important",
    paddingBottom: "3px !important",
    paddingLeft: "0 !important",
    "& code": {
      color: "#000 !important",
      paddingTop: "6px !important",
      paddingBottom: "6px !important",
      paddingLeft: "10px !important",
    },
  },
}));

function getOptionalHeadersForJob(jobType) {
  return apiOptions[jobType].map((option) => {
    if (option.isSubtitle) {
      return {
        isSubtitle: true,
        title: `Optional HTTP Headers for ${option.title}`,
      };
    }

    const messages = [
      <div dangerouslySetInnerHTML={{ __html: option.descriptionHtml }} />,
    ];

    if (option.curlExampleHtml) {
      messages.push(
        <code dangerouslySetInnerHTML={{ __html: option.curlExampleHtml }} />
      );
    } else if (option.curlExample) {
      messages.push(
        <code dangerouslySetInnerHTML={{ __html: option.curlExample }} />
      );
    }

    return {
      name: option.name,
      messages,
    };
  });
}

const renderApiInfo = (apiFeatures, props, isErrorSection = false) => {
  return apiFeatures.map((apiFeature, i) => {
    if (apiFeature.isSubtitle) {
      return (
        <div key={i}>
          <Grid container className={props.classes.row}>
            <Grid item xs={12} sm={12} className={props.classes.cellHeader}>
              <h4 style={{ marginLeft: 20, marginBottom: 0, marginTop: 0 }}>
                {apiFeature.title}
              </h4>
            </Grid>
          </Grid>
        </div>
      );
    }
    const messages = apiFeature.messages.map((message, i) => {
      return <li key={i}>{message}</li>;
    });
    return (
      <div key={i}>
        <Grid container className={props.classes.row}>
          <Grid item xs={12}>
            <div
              style={{ marginLeft: isErrorSection ? 0 : 60 }}
              className={props.classes.cellHeader}
            >
              <code>{apiFeature.name}</code>
            </div>
            <div style={{ marginLeft: isErrorSection ? 0 : 80 }}>
              <ul className={props.classes.apiList}>{messages}</ul>
            </div>
          </Grid>
        </Grid>
      </div>
    );
  });
};

export {
  apiSpecs,
  otherApiClients,
  httpErrorCodes,
  authInfo,
  styleSheet,
  getOptionalHeadersForJob,
  renderApiInfo,
};



// WEBPACK FOOTER //
// ./src/api-shared.js